import * as React from "react";

type Ctx = {
    value?: string;
    onValueChange: (v: string) => void;
    disabled?: boolean;
    open: boolean;
    setOpen: (v: boolean) => void;
    labelsRef: React.MutableRefObject<Record<string, string>>;
};

const SelectCtx = React.createContext<Ctx | null>(null);

export function Select({
    value,
    onValueChange,
    children,
    disabled,
}: {
    value?: string;
    onValueChange: (v: string) => void;
    children: React.ReactNode;
    disabled?: boolean;
}) {
    const [open, setOpen] = React.useState(false);
    const labelsRef = React.useRef<Record<string, string>>({});

    const ctx: Ctx = React.useMemo(
        () => ({ value, onValueChange, disabled, open, setOpen, labelsRef }),
        [value, onValueChange, disabled, open]
    );

    // Close when clicking outside
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        function onDocClick(e: MouseEvent) {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target as Node)) ctx.setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    return (
        <div ref={rootRef} style={{ position: "relative" }}>
            <SelectCtx.Provider value={ctx}>{children}</SelectCtx.Provider>
        </div>
    );
}

export function SelectTrigger({ children }: { children: React.ReactNode }) {
    const ctx = React.useContext(SelectCtx)!;
    return (
        <button
            type="button"
            className="mc-select-trigger"
            tabIndex={ctx.disabled ? -1 : 0}
            aria-disabled={ctx.disabled}
            aria-expanded={ctx.open}
            onClick={() => !ctx.disabled && ctx.setOpen(!ctx.open)}
        >
            {children}
        </button>
    );
}

export function SelectValue({ placeholder }: { placeholder?: string }) {
    const ctx = React.useContext(SelectCtx)!;
    const label =
        (ctx.value && ctx.labelsRef.current[ctx.value]) || placeholder || "";
    return (
        <span className="mc-label" style={{ display: "block", margin: 0 }}>
            {label}
        </span>
    );
}

export function SelectContent({ children }: { children: React.ReactNode }) {
    const ctx = React.useContext(SelectCtx)!;
    if (ctx.disabled || !ctx.open) return null;
    // inline popover; adjust styles as needed
    return (
        <div
            className="mc-select-menu"
            style={{ position: "absolute", left: 0, right: 0, zIndex: 20 }}
            role="listbox"
        >
            {children}
        </div>
    );
}

export function SelectGroup({ children }: { children: React.ReactNode }) {
    return <div>{children}</div>;
}
export function SelectLabel({ children }: { children: React.ReactNode }) {
    return <div className="mc-select-label">{children}</div>;
}

export function SelectItem({
    value,
    children,
}: {
    value: string;
    children: React.ReactNode;
}) {
    const ctx = React.useContext(SelectCtx)!;
    const ref = React.useRef<HTMLButtonElement | null>(null);

    // register label for SelectValue to show
    React.useEffect(() => {
        if (ref.current) {
            ctx.labelsRef.current[value] = ref.current.textContent || value;
        }
    }, [value]);

    const selected = ctx.value === value;

    return (
        <button
            ref={ref}
            type="button"
            role="option"
            aria-selected={selected}
            className="mc-select-item"
            onClick={() => {
                if (ctx.disabled) return;
                ctx.onValueChange(value);
                ctx.setOpen(false);
            }}
        >
            {children}
        </button>
    );
}
